import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { OrgWidgetData, OrgWidgetEntity } from '../../models/org-widget.models';
import { ActivityEntityKind, activityVisibleToViewer } from '../../scope/dashboard-scope';

interface ActivityItem {
    icon: string;
    label: string;
    entityName: string;
    timestamp: Date;
    type: 'created' | 'modified';
    /** Who made the change, when it was recorded (older items carry no name). */
    byName: string;
}

@Component({
    selector: 'shared-recent-activity-widget',
    imports: [CommonModule, MatIconModule],
    template: `
        <div class="activity-list" *ngIf="activities.length > 0; else emptyState">
            <div class="activity-item" *ngFor="let item of activities">
                <div class="activity-icon-wrap" [class]="item.type">
                    <mat-icon>{{ item.icon }}</mat-icon>
                </div>
                <div class="activity-info">
                    <span class="activity-label">{{ item.label }}</span>
                    <span class="activity-entity">{{ item.entityName }}</span>
                    <span class="activity-by" *ngIf="item.byName">by {{ item.byName }}</span>
                </div>
                <span class="activity-time">{{ item.timestamp | date: 'MMM d, h:mm a' }}</span>
            </div>
        </div>

        <ng-template #emptyState>
            <div class="empty-state">
                <mat-icon>history</mat-icon>
                <span>No recent activity</span>
            </div>
        </ng-template>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .activity-list {
                overflow-y: auto;
                scrollbar-width: thin;
                scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

                &::-webkit-scrollbar {
                    width: 6px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                    border-radius: 3px;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 3px;
                    &:hover {
                        background: rgba(0, 0, 0, 0.3);
                    }
                }
            }

            .activity-item {
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 8px 4px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.06);

                &:last-child {
                    border-bottom: none;
                }
            }

            .activity-icon-wrap {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;

                &.created {
                    background: rgba(56, 142, 60, 0.1);
                    mat-icon {
                        color: #388e3c;
                    }
                }

                &.modified {
                    background: rgba(25, 118, 210, 0.1);
                    mat-icon {
                        color: #1976d2;
                    }
                }

                mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                }
            }

            .activity-info {
                flex: 1;
                display: flex;
                flex-direction: column;
                min-width: 0;
            }

            .activity-label {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.54);
            }

            .activity-entity {
                font-size: 13px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.87);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .activity-by {
                font-size: 11px;
                color: rgba(0, 0, 0, 0.54);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .activity-time {
                font-size: 11px;
                color: rgba(0, 0, 0, 0.38);
                white-space: nowrap;
                flex-shrink: 0;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                padding: 24px;
                color: rgba(0, 0, 0, 0.38);

                mat-icon {
                    font-size: 36px;
                    width: 36px;
                    height: 36px;
                }

                span {
                    font-size: 13px;
                }
            }

            /* Dark theme */
            :host-context(.dark-theme) {
                .activity-item {
                    border-bottom-color: rgba(255, 255, 255, 0.08);
                }
                .activity-label {
                    color: rgba(255, 255, 255, 0.54);
                }
                .activity-entity {
                    color: rgba(255, 255, 255, 0.87);
                }
                .activity-time {
                    color: rgba(255, 255, 255, 0.38);
                }
                .activity-by {
                    color: rgba(255, 255, 255, 0.54);
                }
                .empty-state {
                    color: rgba(255, 255, 255, 0.38);
                }
                .activity-list {
                    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
                    &::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.2);
                        &:hover {
                            background: rgba(255, 255, 255, 0.3);
                        }
                    }
                }
            }
        `,
    ],
})
export class RecentActivityWidgetComponent implements OnChanges {
    @Input() data!: OrgWidgetData;
    public activities: ActivityItem[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] && this.data) {
            this.buildActivityList();
        }
    }

    private buildActivityList(): void {
        const items: ActivityItem[] = [];

        // Collect timestamps from all entity types
        this.collectFromEntities(items, this.data.hats, 'Hat', 'work', 'hat');
        this.collectFromEntities(items, this.data.users, 'User', 'person', 'user');
        this.collectFromEntities(items, this.data.securityGroups, 'Security Group', 'security', 'securityGroup');
        this.collectFromEntities(items, this.data.policies, 'Policy', 'policy', 'policy');
        this.collectFromEntities(items, this.data.features, 'Feature', 'extension', 'feature');

        // Sort by most recent first
        items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        // Take top 20
        this.activities = items.slice(0, 20);
    }

    private collectFromEntities(
        items: ActivityItem[],
        entities: OrgWidgetEntity[],
        entityType: string,
        icon: string,
        kind: ActivityEntityKind
    ): void {
        if (!entities) return;
        for (const entity of entities) {
            const d = entity.data as Record<string, unknown>;
            if (!d) continue;
            // Staff see themselves and the people under them; org admins see everyone (ISS-022).
            if (!activityVisibleToViewer(kind, entity.key, d, this.data.viewer)) continue;
            const byName = this.changedByName(d);
            const name = (d['name'] || d['userName'] || d['email'] || entity.key) as string;

            if (d['modifiedOn'] || d['modifiedTimeStamp']) {
                const ts = this.toDate(d['modifiedOn'] || d['modifiedTimeStamp']);
                if (ts) {
                    items.push({
                        icon,
                        label: `${entityType} modified`,
                        entityName: name,
                        timestamp: ts,
                        type: 'modified',
                        byName,
                    });
                }
            } else if (d['createdOn']) {
                const ts = this.toDate(d['createdOn']);
                if (ts) {
                    items.push({
                        icon,
                        label: `${entityType} created`,
                        entityName: name,
                        timestamp: ts,
                        type: 'created',
                        byName,
                    });
                }
            }
        }
    }

    /**
     * Who changed it: the `lastChangedBy` stamp, else the person behind the older `modifiedBy` /
     * `createdBy` ids, which several edit paths still write alone (a hat edit, 28 Oct).
     */
    private changedByName(d: Record<string, unknown>): string {
        const by = d['lastChangedBy'] as Record<string, unknown> | undefined;
        const name = by && typeof by['name'] === 'string' ? by['name'].trim() : '';
        if (name) return name;
        const modified = !!(d['modifiedOn'] || d['modifiedTimeStamp']);
        const uid = (modified ? d['modifiedBy'] : d['createdBy']) as string | undefined;
        return uid ? this.userName(uid) : '';
    }

    private userName(uid: string): string {
        const user = (this.data.users || []).find((u) => u.key === uid)?.data as Record<string, unknown> | undefined;
        const name = user && (user['name'] || user['userName']);
        return typeof name === 'string' ? name.trim() : '';
    }

    private toDate(value: unknown): Date | null {
        if (!value) return null;
        const v = value as Record<string, unknown>;
        // Firestore Timestamp
        if (v['_seconds'] || v['seconds']) {
            return new Date(((v['_seconds'] || v['seconds']) as number) * 1000);
        }
        // Date or ISO string
        if (value instanceof Date) return value;
        if (typeof value === 'string' || typeof value === 'number') {
            const d = new Date(value);
            return isNaN(d.getTime()) ? null : d;
        }
        // Firestore Timestamp with toDate()
        if (typeof (v as { toDate?: () => Date })['toDate'] === 'function') {
            return (v as { toDate: () => Date }).toDate();
        }
        return null;
    }
}
